import { useCallback, useContext, useEffect, useState, Suspense, lazy } from 'react'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { ErrorsContext } from '../contexts/errors_context'
import { blogsRepository } from '../repositories/blogs_repository'
import { Blog } from '../types'
import { Link } from 'react-router-dom'

const Loading = () => {
  const { t } = useTranslation()

  return (
    <div className="text-center">
      <div className="spinner-border" role="status">
        <span className="sr-only">{ t('app.loading') }</span>
      </div>
    </div>
  )
}

type Props = Readonly<{
  userId: number
  canEdit: boolean
}>

const BlogsIndex = ({ userId, canEdit }: Props) => {
  const { t } = useTranslation()
  const { addError } = useContext(ErrorsContext)
  const BlogView = lazy(() => import('./blog_view').then(module => ({ default: module.BlogView })))

  const [blogs, setBlogs] = useState<Blog[]>([])
  const [blogsLoading, setBlogsLoading] = useState<boolean>(false)

  const getBlogs = useCallback((uid: number) => {
    setBlogsLoading(true)

    blogsRepository
      .index(uid)
      .then(({ blogs }) => {
        setBlogs(blogs)
      })
      .catch((err: AxiosError) => {
        addError?.(err)
      })
      .finally(() => {
        setBlogsLoading(false)
      })
  }, [addError])

  useEffect(() => {
    getBlogs(userId)
  }, [userId, getBlogs])

  return (
    <>
      {
        blogsLoading
          ? <Loading />
          : <>
            {
              canEdit
                ? <div className='blog semi-narrow none'>
                  <Link to={ `/u/${userId}/blog/new` } className="button tool">{ t('blog.write') }</Link>
                  <hr />
                </div>
                : null
            }
            {
              blogs.length === 0
                ? (
                  <div className='blog semi-narrow none'>
                    <h2>{ t('blog.title') }</h2>
                    <p className="">{ t('blog.empty') }</p>
                  </div>
                  )
                : <div className="blog semi-narrow">
                  {
                    blogs.map((blog) => (
                      <Suspense key={ blog.id } fallback={<div>{ t('app.loading') }</div>}>
                        <BlogView id={ blog.id ?? 0 } userId={ blog.userId } _blog={ blog } />
                        <hr />
                      </Suspense>
                    ))
                  }
                </div>
            }
          </>
      }
    </>
  )
}

export { BlogsIndex }
